import { useEffect, useState } from 'react';
import type { Recipient } from '@indipia/shared-features';
import {
  previewPhotoShare, previewErrorMessage,
  type SharePreviewGroup, type SharePreviewResponse,
} from '../api/shareAdapter';

export interface PreviewState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  data?: SharePreviewResponse;
  error?: string;
}

/** 일반(반장·거래처) 미리보기 대상 — 수신자 목록의 첫 번째 비고객 수신자 */
export function pickGeneralRecipient(recipients: Recipient[]): { name: string; recipientType: string } | null {
  const r = recipients.find((x) => x.recipientType !== 'CUSTOMER' && x.name.trim() !== '');
  return r ? { name: r.name, recipientType: r.recipientType } : null;
}

const DEBOUNCE_MS = 450;

interface Args {
  siteId: number | undefined;
  group: SharePreviewGroup;
  /** null 이면 호출하지 않는다(idle) */
  recipient: { name: string; recipientType: string } | null;
  title: string;
  content: string;
  /** 'YYYY-MM-DD' — GENERAL 에서만 서버에 보낸다(고객 만료는 서버 산정) */
  expiresAt: string;
  /** 선택한 사진 장수 — undefined 면 보내지 않는다(서버가 현장 사진 수를 센다) */
  photoCount: number | undefined;
  /** false 면 호출하지 않는다(선택한 사진 0장) */
  hasPhotos: boolean;
}

/**
 * 사진 공유 알림톡 서버 렌더 미리보기(POST /photo/shares/preview) — 그룹마다 한 번씩 조회한다.
 * 문구·버튼·만료는 서버가 준 값만 쓰고 화면에서 조립·계산하지 않는다. 입력 중 호출이 몰리지 않게 디바운스한다.
 */
export function usePhotoSharePreview(a: Args): PreviewState {
  const [state, setState] = useState<PreviewState>({ status: 'idle' });

  const generalReady = a.group !== 'GENERAL' || (a.title.trim() !== '' && a.content.trim() !== '' && a.expiresAt !== '');
  const active = a.siteId != null && a.recipient != null && a.hasPhotos && generalReady;
  const key = active
    ? JSON.stringify([a.siteId, a.group, a.recipient, a.title, a.content, a.group === 'GENERAL' ? a.expiresAt : '', a.photoCount ?? null])
    : null;

  useEffect(() => {
    if (key == null || a.siteId == null || a.recipient == null) { setState({ status: 'idle' }); return; }
    const controller = new AbortController();
    const siteId = a.siteId;
    const recipient = a.recipient;
    setState({ status: 'loading' });
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const data = await previewPhotoShare({
            group: a.group,
            siteId,
            ...(a.photoCount != null ? { photoCount: a.photoCount } : {}),
            recipient,
            ...(a.title.trim() ? { title: a.title } : {}),
            ...(a.content.trim() ? { content: a.content } : {}),
            ...(a.group === 'GENERAL' ? { expiresAt: `${a.expiresAt}T23:59:59` } : {}),
          }, controller.signal);
          if (!controller.signal.aborted) setState({ status: 'ready', data });
        } catch (err) {
          if (!controller.signal.aborted) setState({ status: 'error', error: previewErrorMessage(err) });
        }
      })();
    }, DEBOUNCE_MS);
    return () => { window.clearTimeout(timer); controller.abort(); };
    // key 가 모든 입력을 담고 있어 이것만 의존으로 둔다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
